import React, { useState } from "react";
import PropTypes from "prop-types";
import { filter } from "lodash";
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Button,
} from "@mui/material";
import Scrollbar from "../../../components/scrollbar/Scrollbar";
import Label from "../../../components/label";
import Iconify from "../../../components/iconify";
import { useDispatch, useSelector } from "react-redux";
import { setDialog } from "../../../features/dialog/dialogSlice";
import TenantTableToolbar from "./TenantTableToolbar";
import TenantTableHead from "./TenantTableHead";
import {
  getCurrentOfficesCount,
  getCurrentUserCount,
} from "../../../utils/tenantUtils";
import { useChangeTenantMutation } from "../../../features/users/usersApiSlice";
import {
  selectCurrentUserData,
  setCredentials,
} from "../../../features/auth/authSlice";
import { apiSlice } from "../../../app/api/apiSlice";

const TABLE_HEAD = [
  { id: "tenantName", label: "Hauptpraxis", minWidth: 300 },
  { id: "tenantSelected", label: "", sortable: false, width: 132 },
  { id: "contactPerson", label: "Kontaktperson", minWidth: 170 },
  { id: "userCount", label: "Anzahl Benutzer", width: 120 },
  { id: "officeCount", label: "Anzahl Praxen", width: 120 },
  { id: "createdAt", label: "Erstellt am", width: 160 },
  { id: "selectTenant", label: "", sortable: false, width: 168 },
  { id: "actions", label: "", sortable: false, width: 64 },
];

// Utility functions
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  if (query) {
    return filter(
      stabilizedThis.map((el) => el[0]),
      (tenant) =>
        tenant?.tenantName?.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }

  return stabilizedThis.map((el) => el[0]);
}

function TenantTable({ tenants }) {
  const dispatch = useDispatch();
  const currentUserData = useSelector(selectCurrentUserData);

  const [changeTenant] = useChangeTenantMutation();
  const [open, setOpen] = useState(null);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [orderBy, setOrderBy] = useState("tenantName");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleSelectTenant = async (tenant) => {
    try {
      const result = await changeTenant({ tenantId: tenant._id }).unwrap();
      if (result.type === "success") {
        if (result.accessToken && result.userData) {
          dispatch(setCredentials({ ...result }));
          dispatch(apiSlice.util.resetApiState());
        }
        dispatch(
          setDialog({
            title: "Tenant geändert!",
            type: "SUCCESS",
            data: {
              successText: result?.message ?? "",
            },
          })
        );
      }
    } catch (error) {
      console.log("error :>> ", error);
      dispatch(
        setDialog({
          title: "Tenant konnte nicht geändert werden",
          type: "ERROR",
          data: {
            errorText:
              error?.data?.message ?? "Probleme beim Ändern des Tenants",
          },
        })
      );
    }
  };

  const handleOpenMenu = (event, tenant) => {
    setSelectedTenant(tenant);
    setOpen(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setSelectedTenant(null);
    setOpen(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = tenants.map((n) => n.tenantName);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const filteredUsers = applySortFilter(
    tenants,
    getComparator(order, orderBy),
    filterName
  );

  const isNotFound = !filteredUsers.length && !!filterName;

  return (
    <Card>
      <TenantTableToolbar
        numSelected={selected.length}
        filterName={filterName}
        onFilterName={handleFilterByName}
      />

      <Scrollbar>
        <TableContainer sx={{ minWidth: 1240 }}>
          <Table>
            <TenantTableHead
              order={order}
              orderBy={orderBy}
              headLabel={TABLE_HEAD}
              rowCount={tenants.length}
              numSelected={selected.length}
              onRequestSort={handleRequestSort}
              onSelectAllClick={handleSelectAllClick}
            />
            <TableBody>
              {filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  const { _id, tenantName, contactPerson } = row;
                  const selectedUser = selected.indexOf(tenantName) !== -1;
                  const isCurrentTenant =
                    String(_id) === String(currentUserData?.tenant);
                  return (
                    <TableRow
                      hover
                      key={_id}
                      tabIndex={-1}
                      role="checkbox"
                      selected={selectedUser}
                    >
                      <TableCell padding="checkbox">
                        <></>
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="none"
                        sx={{ minWidth: 300 }}
                      >
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography variant="subtitle2" noWrap>
                            {tenantName}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ width: 132 }}>
                        {isCurrentTenant && (
                          <Label color={"warning"}>AUSGEWÄHLT</Label>
                        )}
                      </TableCell>

                      <TableCell align="left" sx={{ minWidth: 170 }}>
                        {contactPerson}
                      </TableCell>

                      <TableCell align="left" sx={{ width: 120 }}>
                        {getCurrentUserCount(row)}
                      </TableCell>
                      <TableCell align="left" sx={{ width: 120 }}>
                        {getCurrentOfficesCount(row)}
                      </TableCell>

                      <TableCell sx={{ width: 160 }}>
                        {new Date(row?.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ width: 168 }}>
                        <Button
                          variant="contained"
                          size="small"
                          disabled={isCurrentTenant}
                          startIcon={<Iconify icon="eva:swap-outline" />}
                          sx={{
                            minWidth: 146,
                            borderRadius: 999,
                            px: 2,
                            py: 1,
                            whiteSpace: "nowrap",
                            fontWeight: 700,
                            letterSpacing: 0.2,
                            color: "common.white",
                            bgcolor: "#007f8f",
                            boxShadow: "0 10px 22px rgba(0, 127, 143, 0.22)",
                            "&:hover": {
                              bgcolor: "#006b7a",
                              boxShadow:
                                "0 12px 26px rgba(0, 127, 143, 0.3)",
                            },
                            "&.Mui-disabled": {
                              color: "text.disabled",
                              bgcolor: "grey.200",
                              boxShadow: "none",
                            },
                          }}
                          onClick={() => handleSelectTenant(row)}
                        >
                          Auswählen
                        </Button>
                      </TableCell>
                      <TableCell align="right" sx={{ width: 64 }}>
                        <IconButton
                          size="large"
                          color="inherit"
                          onClick={(e) => handleOpenMenu(e, row)}
                        >
                          <Iconify icon={"eva:more-vertical-fill"} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {filteredUsers.length === 0 && (
                <TableRow
                  style={{ height: 53 * (rowsPerPage - tenants.length) }}
                >
                  <TableCell colSpan={TABLE_HEAD.length + 1} />
                </TableRow>
              )}
            </TableBody>

            {isNotFound && (
              <TableBody>
                <TableRow>
                  <TableCell
                    align="center"
                    colSpan={TABLE_HEAD.length + 1}
                    sx={{ py: 3 }}
                  >
                    <Paper
                      sx={{
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="h6" paragraph>
                        Nicht gefunden
                      </Typography>

                      <Typography variant="body2">
                        Keine Ergebnisse gefunden für &nbsp;
                        <strong>&quot;{filterName}&quot;</strong>.
                        <br /> Versuche es mit einem anderen Suchbegriff oder
                        überprüfe die Schreibweise.
                      </Typography>
                    </Paper>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Scrollbar>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={tenants.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            p: 1,
            width: 180,
            "& .MuiMenuItem-root": {
              px: 1,
              typography: "body2",
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem
          disabled={
            !selectedTenant ||
            String(selectedTenant?._id) === String(currentUserData?.tenant)
          }
          onClick={() => {
            handleSelectTenant(selectedTenant);
            handleCloseMenu();
          }}
        >
          <Iconify icon={"eva:swap-fill"} sx={{ mr: 2 }} />
          Tenant auswählen
        </MenuItem>
      </Popover>
    </Card>
  );
}

TenantTable.propTypes = {
  tenants: PropTypes.array,
};

export default TenantTable;
